"use client";

import { useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useSubscription } from "@/features/subscription/queries";
import { SubscriptionPlanId, PaymentSourceType } from "@/features/subscription/types";
import { LEGACY_OFFER_IDS } from "@/config/billing-constants";
import { trialHasEnded, clampTrialDays } from "@/lib/utils/trial";
import { SubscriptionStatus, UserRole } from "@speakai/shared";

export interface PersonalizationContext {
  /**
   * User's onboarding goal (e.g. "meetings", "calls", "interviews"). null if not captured.
   * null means "goal not captured or user predates onboarding flow" — NOT a new-user signal.
   * Consumers MUST default to "general professional" framing when null.
   * Never treat null as "user has not yet activated" — team-owner users with zero uploads
   * on day one are still ICP buyers. See taxonomy.md Cohort × Copy Voice.
   */
  onboardingGoal: string | null;
  /** Raw user.onboarding.jobTitle string. null if not captured. Free-form — consumers bucket. */
  jobTitle: string | null;
  /** Raw user.onboarding.teamSize string (e.g. "1", "2-10", "11-50", "51-200", "201+"). null if not captured. */
  teamSize: string | null;
  /** Raw user.onboarding.usageType ("personal" | "team"). null if not captured. */
  useCaseType: string | null;
  /** Raw user.onboarding.meetingType string. null if not captured. */
  meetingType: string | null;
  /** True if flagged as new user by server. Defaults false — server may not set on all accounts. */
  isNewUser: boolean;
  /**
   * Raw permission.role passthrough. null when absent (treat as solo / unattached account).
   * UserRole.OWNER and UserRole.ADMIN both have billing + team management access;
   * UserRole.MEMBER is a non-billing seat. For billing-gate checks prefer
   * usePermissions().canManageBilling — it folds in plan-expiry and override flags.
   */
  role: UserRole | null;
  /**
   * Payment rail for current subscription. null while loading or on trial/PAYG.
   * PaymentSourceType.Direct_Deposit is derived client-side (server returns null paymentSource
   * on speak_custom + !isExpired); full detection (no active Stripe/Paddle sub confirmation)
   * requires a server-side check. Never pass Direct_Deposit to server APIs.
   */
  paymentSource: PaymentSourceType | null;
  /**
   * Raw subscription status string from MongoDB.
   * Known values: "active" | "paused" | "pendingReview" | "pendingCancellation" |
   *   "cancelled" | "pendingPayment" | "payment_failed" | "past_due".
   * Consumers derive overlays: isWindingDown = status === "pendingCancellation".
   * null while loading.
   */
  subscriptionStatus: SubscriptionStatus | null;
  /**
   * True if subscriber is on a grandfathered offer (speak-starter or speak-team).
   * null while loading. false if on any current offer.
   * PARTIAL DETECTION: Out-of-enum offerIds (e.g. "year-end-2024", "meeting-transcription-2024"
   * — pre-standardization values present on confirmed legacy accounts) return false.
   * Full detection requires createdAt field from /subscription response. See taxonomy.md.
   * Consumers MUST NOT show current pricing copy to users where planIsLegacy === true.
   * Use grandfathered-value framing only. QUILL BLOCKER rule #4.
   */
  planIsLegacy: boolean | null;
  /**
   * True if the user has uploaded at least one media file (totalMedia > 0).
   * null while loading or on error. Consumers MUST check isLoading before acting on null —
   * do not show "first upload" CTAs when value is null.
   * Sourced from Subscription.totalMedia via the same useSubscription() call already in this hook.
   */
  hasActivatedUpload: boolean | null;
  /** Days remaining in trial. null while loading or when not on a trial plan. */
  trialDaysRemaining: number | null;
  /**
   * True when user is on an active (non-expired) trial with 3 or fewer days remaining.
   * null while loading (sub not yet resolved). false when subscription is loaded and
   * user is NOT on a trial plan — null does NOT mean "not a trial user", it means "loading".
   * Consumers MUST check isLoading before acting on null to avoid false negative flashes.
   * Returns null (not false) while loading so callers can distinguish "loading" from "not applicable".
   */
  isTrialNearExpiry: boolean | null;
  /**
   * True while the subscription query has not yet resolved on first mount.
   * TQ v5: isLoading = isPending && isFetching. False during background refetches.
   */
  isLoading: boolean;
  /** Raw Apollo job title. null if enrichment absent. Raw — normalize before display. Do NOT render directly. NEVER pass to dangerouslySetInnerHTML. */
  apolloTitle: string | null;
  /**
   * True when Apollo enrichment completed (enrichedAt present and truthy).
   * false means EITHER (a) never enriched, OR (b) enrichedAt missing — treat as "no Apollo data".
   * Do NOT infer from false that enrichment is pending — it may never arrive.
   */
  apolloEnriched: boolean;
  /** Raw Apollo seniority. Known: "individual_contributor","manager","director","vp","c_suite","owner". null if absent. NEVER pass to dangerouslySetInnerHTML. */
  apolloSeniority: string | null;
  /** Raw Apollo employee range (e.g. "1-10","51-200","201-500","1,001-5,000"). null if absent. */
  apolloCompanySize: string | null;
  /** Raw Apollo industry. null if absent. Raw — consumers bucket/normalize. NEVER pass to dangerouslySetInnerHTML. */
  apolloIndustry: string | null;
  /** Raw Apollo departments. null if absent. Raw — consumers bucket/normalize. NEVER pass to dangerouslySetInnerHTML. */
  apolloDepartments: string | null;
  /** Raw Apollo company name. null if absent. Raw — sanitize before display. NEVER pass to dangerouslySetInnerHTML. */
  apolloCompany: string | null;
  /**
   * Company website URL from Apollo. null if absent or failed https validation.
   * Raw — sanitize before use as href. NEVER pass to dangerouslySetInnerHTML.
   */
  apolloWebsite: string | null;
  /**
   * LinkedIn profile URL from Apollo. null if absent or failed https validation.
   * Raw — sanitize before use as href. NEVER pass to dangerouslySetInnerHTML.
   */
  apolloLinkedin: string | null;
  /**
   * Company LinkedIn URL from Apollo. null if absent or failed https validation.
   * Raw — sanitize before use as href. NEVER pass to dangerouslySetInnerHTML.
   */
  apolloOrgLinkedin: string | null;
  /** Apollo company city. null if absent. NEVER pass to dangerouslySetInnerHTML. */
  apolloCompanyCity: string | null;
  /** Apollo company country. null if absent. NEVER pass to dangerouslySetInnerHTML. */
  apolloCompanyCountry: string | null;
  /** Annual revenue as Number from Apollo. null if absent. Raw — format/bucket for display. */
  apolloAnnualRevenue: number | null;
  /** Total funding as Number from Apollo. null if absent. Raw — format/bucket for display. */
  apolloTotalFunding: number | null;
  /**
   * Comma-joined technology stack (255 char cap). null if absent.
   * Raw — render as text node only. NEVER pass to dangerouslySetInnerHTML.
   */
  apolloTechnologies: string | null;
  /**
   * Comma-joined keywords (255 char cap). null if absent.
   * Raw — render as text node only. NEVER pass to dangerouslySetInnerHTML.
   */
  apolloKeywords: string | null;
}

/**
 * All signals derived from auth store and cached subscription data.
 * Server-side enforces all endpoints independently.
 * Callers MUST NOT gate data fetches or API calls on any signal from this hook.
 */
export function usePersonalizationContext(): PersonalizationContext {
  const { user } = useAuth();
  const { data: sub, isLoading: subLoading } = useSubscription();

  return useMemo(() => {
    // Auth-derived signals (zero cost — no fetch)
    const onboardingGoal = user?.onboarding?.goal ?? null;
    const jobTitle = user?.onboarding?.jobTitle ?? null;
    const teamSize = user?.onboarding?.teamSize ?? null;
    const useCaseType = user?.onboarding?.usageType ?? null;
    const meetingType = user?.onboarding?.meetingType ?? null;
    const isNewUser = user?.isNewUser ?? false;

    // Apollo enrichment signals — raw passthroughs (zero cost — reads from auth store, no fetch)
    const apolloTitle = user?.apollo?.title ?? null;
    const apolloEnriched = !!user?.apollo?.enrichedAt;
    const apolloSeniority = user?.apollo?.seniority ?? null;
    const apolloCompanySize = user?.apollo?.employees ?? null;
    const apolloIndustry = user?.apollo?.industry ?? null;
    const apolloDepartments = user?.apollo?.departments ?? null;
    const apolloCompany = user?.apollo?.company ?? null;
    const apolloWebsite = user?.apollo?.websiteUrl ?? null;
    const apolloLinkedin = user?.apollo?.linkedinUrl ?? null;
    const apolloOrgLinkedin = user?.apollo?.orgLinkedinUrl ?? null;
    const apolloCompanyCity = user?.apollo?.companyCity ?? null;
    const apolloCompanyCountry = user?.apollo?.companyCountry ?? null;
    const apolloAnnualRevenue = user?.apollo?.annualRevenue ?? null;
    const apolloTotalFunding = user?.apollo?.totalFunding ?? null;
    const apolloTechnologies = user?.apollo?.technologies ?? null;
    const apolloKeywords = user?.apollo?.keywords ?? null;

    const role = user?.permission?.role ?? null;

    // Subscription-derived signals
    let paymentSource: PaymentSourceType | null = null;
    let subscriptionStatus: SubscriptionStatus | null = null;
    let planIsLegacy: boolean | null = null;
    let hasActivatedUpload: boolean | null = null;
    let trialDaysRemaining: number | null = null;
    let isTrialNearExpiry: boolean | null = null;

    if (sub) {
      const isTrial = sub.planId === SubscriptionPlanId.Speak_Free_Trial;

      // paymentSource typed non-nullable in UserSubscription (payment.ts:117) but arrives
      // null at runtime for direct-deposit accounts — pre-standardization interface gap.
      // Fix requires widening paymentSource to `PaymentSourceType | null` in payment.ts,
      // coordinated with ATLAS (shared type, many consumers). Deferred per plan Out of Scope.
      const src = sub.paymentSource as PaymentSourceType | null | undefined;
      if (
        src === PaymentSourceType.Stripe ||
        src === PaymentSourceType.Paddle ||
        src === PaymentSourceType.Revenuecat
      ) {
        paymentSource = src;
      } else if (
        sub.planId === SubscriptionPlanId.Speak_Custom &&
        !sub.isExpired &&
        sub.status !== SubscriptionStatus.Cancelled
      ) {
        // Partial direct-deposit detection. INTENTIONALLY broader than taxonomy spec
        // ({active, pendingPayment, pendingCancellation}): a Paused or PendingReview direct-deposit
        // account is still on this rail — billing chrome must still hide update-card CTAs and show
        // "Contact sales". The taxonomy's positive allowlist would drop these accounts to null
        // (trial chrome), which is incorrect. We exclude ONLY the unambiguous terminal state.
        // isExpired === true is the authoritative terminal guard.
        // Full detection (absent Stripe/Paddle sub confirmation) deferred to Phase C issue #5.
        paymentSource = PaymentSourceType.Direct_Deposit;
      }

      subscriptionStatus = (sub.status as SubscriptionStatus) ?? null;

      // planId gate is intentional: PAYG subscribers can have offerId "speak-starter"
      // (pre-standardization) and must not be classified as legacy.
      planIsLegacy =
        sub.planId === SubscriptionPlanId.Speak_Custom &&
        LEGACY_OFFER_IDS.includes(sub.offerId);

      // Return null (not false) when totalMedia is absent — distinguishes "server omitted
      // the field" from "user has zero uploads". Consumers must check isLoading first.
      hasActivatedUpload =
        sub.totalMedia !== undefined ? sub.totalMedia > 0 : null;
      trialDaysRemaining =
        isTrial && sub.remainingDays != null ? clampTrialDays(sub.remainingDays) : null;
      isTrialNearExpiry =
        isTrial &&
        !trialHasEnded(sub.isExpired, sub.remainingDays) &&
        (sub.remainingDays ?? Infinity) <= 3;
    }

    return {
      onboardingGoal,
      jobTitle,
      teamSize,
      useCaseType,
      meetingType,
      isNewUser,
      role,
      paymentSource,
      subscriptionStatus,
      planIsLegacy,
      hasActivatedUpload,
      trialDaysRemaining,
      isTrialNearExpiry,
      isLoading: subLoading,
      apolloTitle,
      apolloEnriched,
      apolloSeniority,
      apolloCompanySize,
      apolloIndustry,
      apolloDepartments,
      apolloCompany,
      apolloWebsite,
      apolloLinkedin,
      apolloOrgLinkedin,
      apolloCompanyCity,
      apolloCompanyCountry,
      apolloAnnualRevenue,
      apolloTotalFunding,
      apolloTechnologies,
      apolloKeywords,
    };
  }, [user, sub, subLoading]);
}
